import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, X, PenSquare } from "lucide-react";
import { REVIEWS } from "../../data/reviews";
import { USE_CASES } from "../../data/useCases";
import { GUIDES } from "../../data/guides";
import { SITE } from "../../constants/testIds";

const NavDropdown = ({ label, items, basePath, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button data-testid={testId} className="flex items-center gap-1 text-sm text-zinc-300 hover:text-white transition-colors py-2">
        {label}
        <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.18 }}
            className="absolute top-full left-1/2 -translate-x-1/2 pt-2 w-72"
          >
            <div className="bg-[#0D0E1A] border border-white/10 rounded-sm p-2 shadow-2xl">
              {items.map((item) => (
                <Link
                  key={item.slug}
                  to={`${basePath}/${item.slug}`}
                  className="block px-3 py-2.5 rounded-sm text-sm text-zinc-300 hover:text-white hover:bg-white/5 transition-colors"
                >
                  {item.title || item.name}
                </Link>
              ))}
              <Link to={basePath} className="block px-3 py-2.5 mt-1 border-t border-white/10 rounded-sm text-xs font-mono-tag uppercase tracking-wider text-primary hover:text-[#5C7CFA] transition-colors">
                View all →
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLinks = [
    { label: "Comparison", to: "/comparison", testId: SITE.navComparison },
    { label: "Resources", to: "/resources", testId: SITE.navResources },
    { label: "About", to: "/about", testId: SITE.navAbout },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "glass-nav border-b border-white/10" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid={SITE.headerLogo} className="flex items-center gap-2 group">
          <span className="w-8 h-8 rounded-sm bg-primary flex items-center justify-center">
            <PenSquare className="w-4 h-4 text-white" aria-hidden="true" />
          </span>
          <span className="font-display font-bold text-white text-base tracking-tight">
            AI Writing<span className="text-gradient-gold">Platforms</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          <NavDropdown label="Reviews" items={REVIEWS} basePath="/reviews" testId={SITE.navReviews} />
          <NavDropdown label="By Use Case" items={USE_CASES} basePath="/use-cases" testId={SITE.navUseCases} />
          <NavDropdown label="Guides" items={GUIDES} basePath="/guides" testId={SITE.navGuides} />
          {navLinks.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={l.testId} className={({ isActive }) => `text-sm transition-colors py-2 ${isActive ? "text-white" : "text-zinc-300 hover:text-white"}`}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <Link to="/resources" data-testid={SITE.headerCta} className="hidden lg:inline-flex h-10 px-5 items-center bg-primary hover:bg-[#5C7CFA] text-white text-sm font-medium rounded-sm transition-colors">
          Free Prompts Library
        </Link>

        <button data-testid={SITE.mobileMenuTrigger} onClick={() => setMobileOpen(true)} className="lg:hidden text-white">
          <Menu className="w-6 h-6" aria-hidden="true" />
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="lg:hidden fixed inset-0 z-50 bg-inkblack/98 backdrop-blur-xl overflow-y-auto"
          >
            <div className="flex items-center justify-between h-16 px-6 border-b border-white/10">
              <span className="font-display font-bold text-white">Menu</span>
              <button onClick={() => setMobileOpen(false)} className="text-white" aria-label="Close menu"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <p className="font-mono-tag text-xs uppercase tracking-wider text-gold mb-3">Reviews</p>
                {REVIEWS.map((r) => (
                  <Link key={r.slug} to={`/reviews/${r.slug}`} onClick={() => setMobileOpen(false)} className="block py-2 text-zinc-300">{r.title.split(":")[0]}</Link>
                ))}
              </div>
              <div>
                <p className="font-mono-tag text-xs uppercase tracking-wider text-gold mb-3">By Use Case</p>
                {USE_CASES.map((u) => (
                  <Link key={u.slug} to={`/use-cases/${u.slug}`} onClick={() => setMobileOpen(false)} className="block py-2 text-zinc-300">{u.title}</Link>
                ))}
              </div>
              <div>
                <p className="font-mono-tag text-xs uppercase tracking-wider text-gold mb-3">Guides</p>
                {GUIDES.map((g) => (
                  <Link key={g.slug} to={`/guides/${g.slug}`} onClick={() => setMobileOpen(false)} className="block py-2 text-zinc-300">{g.title}</Link>
                ))}
              </div>
              <div className="border-t border-white/10 pt-6 space-y-3">
                {[["Comparison Matrix", "/comparison"], ["Resources", "/resources"], ["About", "/about"]].map(([label, to]) => (
                  <Link key={to} to={to} onClick={() => setMobileOpen(false)} className="block py-2 text-white font-medium">{label}</Link>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
